import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrendingUp, FiTool, FiPlayCircle, FiStar, FiChevronRight } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';

export const Dashboard = () => {
  const { user, certs, cases } = useAppContext();
  const navigate = useNavigate();
  const activeCert = certs[0];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Welcome Hero */}
      <div className="glass-panel p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        
        <div className="relative z-10">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Ready for the field, {user.name.split(' ')[0]}?</h1>
          <p className="text-slate-400 mb-6">You're on a {user.streak}-day learning streak. Keep it up!</p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={() => navigate('/troubleshoot')} className="btn-primary flex-1 py-3 shadow-lg shadow-blue-600/20">
              <FiTool /> Troubleshoot Issue
            </button>
            <button onClick={() => navigate('/academy')} className="btn-secondary flex-1 py-3">
              <FiPlayCircle /> Continue Training
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Stats Card */}
        <div className="glass-panel p-5 rounded-2xl flex flex-col justify-between">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-slate-400 text-sm font-medium">Current Level</h3>
              <div className="text-3xl font-bold text-white mt-1">Lvl {user.level}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30 text-amber-400 text-xl">
              <FiStar />
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-xs text-slate-400 mb-2">
              <span>{user.xp} XP</span>
              <span>{user.nextLevelXp} XP</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-cyan-400 h-2 rounded-full transition-all duration-1000"
                style={{ width: `${(user.xp / user.nextLevelXp) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-right">{user.nextLevelXp - user.xp} XP to next level</p>
          </div>
        </div>

        {/* Cert Progress */}
        <div className="glass-panel p-5 rounded-2xl cursor-pointer hover:border-slate-600 transition-colors" onClick={() => navigate('/certifications')}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-slate-200 font-semibold">{activeCert.title}</h3>
            <span className="text-blue-400 text-sm font-medium">{activeCert.progress}%</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2 mb-4 overflow-hidden">
             <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${activeCert.progress}%` }}></div>
          </div>
          <p className="text-sm text-slate-400 mb-4">{activeCert.completedModules} of {activeCert.totalModules} modules completed</p>
          <div className="flex items-center text-blue-400 text-sm font-medium hover:text-blue-300">
            View Certification Track <FiChevronRight className="ml-1" />
          </div>
        </div>
      </div>

      {/* Recent Cases */}
      <div>
        <div className="flex justify-between items-end mb-4">
          <h2 className="text-lg font-bold text-white">Recent Field Cases</h2>
          <button onClick={() => navigate('/history')} className="text-sm text-blue-400 hover:text-blue-300">View All</button>
        </div>
        <div className="space-y-3">
          {cases.slice(0, 3).map((c, i) => (
            <div key={i} className="glass-panel p-4 rounded-xl flex items-center justify-between hover:bg-slate-800 transition-colors cursor-pointer">
              <div className="flex items-center gap-4">
                <img src={c.image} alt="Case" className="w-12 h-12 rounded-lg object-cover border border-slate-700" />
                <div>
                  <h4 className="font-medium text-slate-200">{c.issue}</h4>
                  <p className="text-xs text-slate-500">{c.date} • {c.id}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium border ${
                c.status === 'resolved' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}>
                {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};